/**
 * @file src/components/OrganizationSearchDropdown/OrganizationSearchDropdown.tsx
 * @exports OrganizationSearchDropdown
 * @summary Organization picker: a dropdown under its trigger, with search state scoped to
 * each opening.
 * @remarks Keep the UI aligned with its twin in cortex.
 */
import { CheckmarkCircle, colors, Search } from "@cerebruminc/cerebellum";
import { CloseButton, Popover, ScrollArea, Skeleton, TextInput } from "@mantine/core";
import { useAtomValue } from "jotai";
import { type FC, type ReactElement, type UIEvent, useEffect, useRef, useState } from "react";

import { ALL_ORGS_TEST_ID, getOrgSelectorTestId } from "src/helpers/organization";
import { useOrganizationSearch } from "src/hooks";
import { highContrastThemeAtom } from "src/shame/shame-atoms";

import styles from "./OrganizationSearchDropdown.module.css";

const LOAD_MORE_THRESHOLD_PX = 80;
const DROPDOWN_OFFSET_PX = 8;
// A placeholder for a name stands as tall as the name, not as tall as the row.
const SKELETON_TEXT_HEIGHT_PX = 16;
/*
 * The loading state stands in for the list, so it is as long as the list was — the way cerebellum's
 * UserCard sizes its own. Capped at what the scroller actually shows, since placeholders below the
 * fold say nothing, and floored at two so a reload never looks like a single stray row.
 * These mirror .resultsScroller's height and .resultsList's bottom padding; keep them in step.
 */
/*
 * Below this, every organization fits on screen and a search field is noise — the same threshold
 * cerebellum's UserCard applies with its orgSearchThreshold default.
 */
const SEARCH_THRESHOLD = 10;
const MIN_SKELETON_ROWS = 2;
const ROW_HEIGHT_PX = 49;
const SCROLLER_HEIGHT_PX = 320;
const LIST_BOTTOM_PADDING_PX = 32;
const MAX_SKELETON_ROWS = Math.max(
	MIN_SKELETON_ROWS,
	Math.ceil((SCROLLER_HEIGHT_PX - LIST_BOTTOM_PADDING_PX) / ROW_HEIGHT_PX),
);
// Wide enough for organization names. Mantine rem-scales the `width` prop, so a CSS
// expression cannot go there; the phone-screen clamp is an ordinary style instead.
const DROPDOWN_WIDTH = 420;
/*
 * The bottom gutter is zero and lives
 * inside the scroller instead, so a row leaving the list is clipped by the edge of the
 * dropdown rather than fading out short of it.
 */
const DROPDOWN_STYLES = { dropdown: { maxWidth: "calc(100vw - 32px)", padding: "24px 24px 0" } };

// The scrollbar thumb colour is written inline by the cerebellum theme, so high contrast has
// to override it the same way.
const HIGH_CONTRAST_SCROLLBAR = {
	scrollbar: {
		"--cb-scrollarea-thumb-active-color": colors.BLACK,
		"--cb-scrollarea-thumb-color": colors.BLACK,
		"--cb-scrollarea-thumb-hover-color": colors.BLACK,
	},
};

interface OrganizationSearchDropdownProps {
	/** Currently selected organization id, or null for "All Organizations". */
	activeOrgId: string | null;
	/** The trigger the dropdown hangs from. Receives the popover ref, so it must be an element. */
	children: ReactElement;
	onClose: () => void;
	onSelect: (response: { organizationId: string | null }) => void;
	opened: boolean;
}

/** Search state lives and dies with each opening: Popover unmounts its dropdown when closed. */
export const OrganizationSearchDropdown: FC<OrganizationSearchDropdownProps> = ({
	children,
	opened,
	onClose,
	...props
}) => (
	<Popover
		// The trigger sits in a fixed header and is never detached, and the detach check
		// measures the target, which reports zero size outside a browser.
		hideDetached={false}
		offset={DROPDOWN_OFFSET_PX}
		onChange={(isOpen) => {
			if (!isOpen) onClose();
		}}
		opened={opened}
		position="bottom-start"
		/*
		 * Selecting a row unmounts the focused button, which would otherwise drop focus to the
		 * document. Mantine already returns focus on Escape; this covers selection too.
		 */
		returnFocus
		shadow="md"
		styles={DROPDOWN_STYLES}
		trapFocus
		width={DROPDOWN_WIDTH}
		/*
		 * Mantine would name the panel from its target, which reads out as whichever organization
		 * is selected. This makes a mess for screen reader users, so we label the panel explicitly.
		 */
		withRoles={false}
	>
		<Popover.Target>{children}</Popover.Target>
		<Popover.Dropdown aria-label="Find an organization" role="dialog" tabIndex={-1}>
			{opened && <OrganizationSearch {...props} />}
		</Popover.Dropdown>
	</Popover>
);

const OrganizationSearch = ({
	activeOrgId,
	onSelect,
}: Pick<OrganizationSearchDropdownProps, "activeOrgId" | "onSelect">) => {
	const [searchTerm, setSearchTerm] = useState("");
	// Cognition applies high contrast per component: the Mantine theme only carries it on the
	// vID and auth routes, and the picker lives in the app header.
	const isHighContrast = useAtomValue(highContrastThemeAtom);
	const iconFill = isHighContrast ? colors.BLACK : "var(--mantine-color-gray-7)";
	const checkmarkFill = isHighContrast ? colors.BLACK : colors.AQUA_75;

	const { activeTerm, hasMore, isError, isFetching, loadMore, organizations } = useOrganizationSearch({ searchTerm });

	/*
	 * Fetching page one for a term the list does not belong to yet — a new search, or the very
	 * first open. Paging in more of the same term is not this: those rows stay on screen.
	 */
	const isReloading = isFetching && (organizations.length === 0 || searchTerm.trim() !== activeTerm);
	// How long the list was before it went away, which is how long its placeholder should be.
	const lastRowCount = useRef(MIN_SKELETON_ROWS);
	useEffect(() => {
		if (organizations.length > 0) lastRowCount.current = organizations.length;
	}, [organizations.length]);
	const skeletonCount = Math.min(Math.max(lastRowCount.current, MIN_SKELETON_ROWS), MAX_SKELETON_ROWS);

	/*
	 * A replaced list starts at the top. Keyed on the searched term rather than on the rows, so
	 * appending a page leaves the scroller where the reader left it. Without this the browser puts
	 * the old offset back once the results make the list tall again — see overflow-anchor in the
	 * stylesheet, which stops it doing the same thing behind this reset.
	 */
	const viewportRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (viewportRef.current) viewportRef.current.scrollTop = 0;
	}, [activeTerm]);

	/*
	 * Decided once, from the unsearched list: a page of 25 that reports more behind it, or a short
	 * page that is the whole account. It must not follow the filtered results — a search that
	 * narrows to three rows would pull the field out from under the term still in it.
	 */
	const hasManyOrgs = useRef<boolean | undefined>(undefined);
	if (hasManyOrgs.current === undefined && !isReloading && !isError && !activeTerm) {
		hasManyOrgs.current = hasMore || organizations.length > SEARCH_THRESHOLD;
	}
	const showSearch = hasManyOrgs.current === true;
	// With no search field the list is the whole account, so it is sized to its rows rather than
	// scrolled: no fold to run past, and no empty box under a short list.
	const fitsWithoutScrolling = hasManyOrgs.current === false;

	const handleScroll = (event: UIEvent<HTMLDivElement>) => {
		const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
		// isError stops a rejected page being re-requested on every scroll event; "Try again" owns the retry.
		if (hasMore && !isFetching && !isError && scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX) {
			loadMore();
		}
	};

	// Part of the list, so it goes away with the list while its placeholder is up.
	const showAllOrgsRow = !activeTerm && !isReloading;
	const isEmpty = !isError && !isReloading && organizations.length === 0;

	const renderRow = ({ id, name }: { id: string | null; name: string }) => {
		const isActive = activeOrgId === id;
		return (
			<button
				aria-current={isActive || undefined}
				className={`${styles.orgRow} ${isActive ? styles.orgRowActive : ""}`}
				data-org-id={id ?? undefined}
				data-testid={id === null ? ALL_ORGS_TEST_ID : getOrgSelectorTestId({ name })}
				key={id ?? "all"}
				onClick={() => onSelect({ organizationId: id })}
				// Nothing to select: tabbing moves past the row you are already on.
				tabIndex={isActive ? -1 : undefined}
				type="button"
			>
				<span className={styles.orgName} data-sentry-unmask={id === null ? true : undefined}>
					{name}
				</span>
				{isActive && (
					<span className={`${styles.iconBox} ${styles.activeIcon}`}>
						<CheckmarkCircle fill={checkmarkFill} />
					</span>
				)}
			</button>
		);
	};

	return (
		<div
			className={styles.panel}
			data-fit-content={fitsWithoutScrolling || undefined}
			data-high-contrast={isHighContrast || undefined}
			data-testid="org-search-panel"
		>
			{showSearch && (
				<TextInput
					aria-label="Search organizations"
					autoFocus
					data-testid="org-search-input"
					leftSection={
						<span className={styles.iconBox}>
							<Search fill={iconFill} />
						</span>
					}
					onChange={(event) => setSearchTerm(event.currentTarget.value)}
					placeholder="Search organizations"
					rightSection={
						searchTerm ? (
							<CloseButton aria-label="Clear search" onClick={() => setSearchTerm("")} size="sm" />
						) : undefined
					}
					value={searchTerm}
				/>
			)}
			{/* An always-visible scrollbar is the only cue that a list runs past the fold, but a list
			    that fits has no fold, and a bar beside it would be pointing at nothing. */}
			<ScrollArea
				className={styles.resultsScroller}
				offsetScrollbars={fitsWithoutScrolling ? false : "y"}
				// The list is the scroller. Without this, a wheel that reaches the end of a long
				// organization list keeps going and scrolls the page underneath the dropdown.
				overscrollBehavior="contain"
				scrollbars="y"
				styles={isHighContrast ? HIGH_CONTRAST_SCROLLBAR : undefined}
				type={fitsWithoutScrolling ? "never" : "always"}
				viewportProps={{ "aria-label": "Organization results", onScroll: handleScroll, role: "region" }}
				viewportRef={viewportRef}
			>
				<div aria-busy={isReloading || undefined} className={styles.resultsList}>
					{showAllOrgsRow && renderRow({ id: null, name: "All Organizations" })}
					{isReloading
						? Array.from({ length: skeletonCount }, (_, index) => (
								<div aria-hidden="true" className={styles.skeletonRow} key={`org-loading-skeleton-${index}`}>
									<Skeleton data-testid="org-search-skeleton" height={SKELETON_TEXT_HEIGHT_PX} radius={4} />
								</div>
							))
						: organizations.map((org) => renderRow({ id: org.id, name: org.name }))}
					{isEmpty && (
						<div className={styles.emptyState} data-testid="org-search-empty" role="status">
							No organizations found{activeTerm ? ` for “${activeTerm}”` : ""}.
						</div>
					)}
					{isError && (
						<div className={styles.footerHint} role="alert">
							Could not load organizations.{" "}
							<button type="button" disabled={isFetching} onClick={() => loadMore()}>
								Try again
							</button>
						</div>
					)}
				</div>
			</ScrollArea>
		</div>
	);
};
